import React, { useContext } from "react"
import { Span } from "../styles/TextStyles.styled"
import dark from "../../theme/dark"
import light from "../../theme"
import ArrowedLink from "../Links/ArrowedLink"
import DatoCmsText from "../DatoCmsText/DatoCmsText"
import Img from "gatsby-image/withIEPolyfill"
import * as S from "./CarouselCard.styled"
import { urlFor } from "../../util/content-links"
import { graphql, navigate, useStaticQuery } from "gatsby"
import { DomainContext } from "../../context/DomainContext"
import { DomainAwareThemeProvider } from "../DomainAwareThemeProvider"

const Overline = ({ __typename, overline }) =>
  __typename === "DatoCmsJobOffer" || overline ? <Span>{overline}</Span> : null

const getTheme = ({ __typename, style, useAlternativeStyle }) =>
  __typename === "DatoCmsJobOffer"
    ? useAlternativeStyle
      ? "Dark blue"
      : "Light blue"
    : style || "Plain"

const Image = ({ __typename, image, logo, styleAsBrand }) => {
  if (logo) {
    return (
      <S.BrandImgWrapper>
        <img src={logo.url} alt={logo.alt} />
      </S.BrandImgWrapper>
    )
  }
  if (!image) return null
  const Wrapper = styleAsBrand ? S.BrandImgWrapper : React.Fragment

  return (
    <Wrapper>
      {image.fluid ? (
        <Img {...image} objectFit={"contain"} objectPosition={"left"} />
      ) : (
        <img src={image.url} alt={image.url} />
      )}
    </Wrapper>
  )
}

const typeSpecificClassName = typename =>
  typename === "DatoCmsBrand" ? "brand" : null

const hasLink = item => item.page || item.slug

const getUrl = (item, domainContext) => {
  if (item.page) {
    return urlFor(
      "DatoCmsPage",
      item.page.locale,
      item.page.slug,
      "",
      domainContext,
      item.page.domain?.originalId
    )
  }
  return urlFor(item.__typename, item.locale, item.slug, "", domainContext)
}

const onCardClick = (item, domainContext) => {
  if (!hasLink(item)) return
  const url = getUrl(item, domainContext)
  if (url.startsWith("/")) navigate(url)
  else window.open(url, "_blank")
}

const CardLink = ({ item, domainContext, labels }) => {
  const url = getUrl(item, domainContext)
  return item.cta ? (
    <ArrowedLink
      hideBackground
      onClick={e => e.stopPropagation()}
      ctaData={item.cta}
    />
  ) : item.page ? (
    <ArrowedLink
      onClick={e => e.stopPropagation()}
      hideBackground
      link={{
        label: labels.learnMore,
        url: url,
        external: !url.startsWith("/"),
      }}
    />
  ) : item.slug ? (
    <ArrowedLink
      hideBackground
      onClick={e => e.stopPropagation()}
      link={{
        external: !url.startsWith("/"),
        label:
          item.__typename === "DatoCmsJobOffer"
            ? labels.viewJobOffer
            : labels.learnMore,
        url: url,
      }}
    />
  ) : null
}

const CarouselCard = ({ reference, item }) => {
  const { allDatoCmsTranslation } = useStaticQuery(graphql`
    {
      allDatoCmsTranslation {
        edges {
          node {
            tags
            learnMore
            locale
            viewJobOffer
          }
        }
      }
    }
  `)
  const labels = allDatoCmsTranslation.edges.find(
    edge => edge.node.locale === item.locale
  ).node
  const domainContext = useContext(DomainContext)
  return (
    <DomainAwareThemeProvider
      theme={!getTheme(item).includes("Plain") ? dark : light}
    >
      <S.Card
        ref={reference}
        styleVariation={getTheme(item)}
        className={typeSpecificClassName(item.__typename)}
        clickable={hasLink(item)}
        onClick={() => onCardClick(item, domainContext)}
        style={{ maxWidth: item.__typename === "DatoCmsJobOffer" && "350px" }}
      >
        <Overline {...item} />
        {item.title && (
          <S.Title forJobOffer={item.__typename === "DatoCmsJobOffer"}>
            {item.title}
          </S.Title>
        )}
        <Image {...item} />
        <Span as="div" className="description">
          <DatoCmsText
            text={item.description}
            textNode={item.descriptionNode}
          />
        </Span>
        <CardLink domainContext={domainContext} item={item} labels={labels} />
      </S.Card>
    </DomainAwareThemeProvider>
  )
}

export default React.forwardRef((props, ref) => {
  return <CarouselCard {...props} reference={ref} />
})
